import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { 
  X, ChevronRight, ChevronLeft, Home, Target, DollarSign, 
  Users, GraduationCap, Wrench, Rocket, Shield, Briefcase,
  MessageSquare, CheckCircle
} from 'lucide-react';

// Tour steps configuration - different for students vs parents
const PARENT_TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Agentic FuturePath AI! 🎉',
    description: 'This quick tour will show you how to support your children\'s educational journey. It only takes 2 minutes!',
    icon: Home,
    position: 'center'
  },
  {
    id: 'dashboard',
    title: 'Your Family Dashboard',
    description: 'This is your home base. See all your children\'s progress at a glance, including their readiness scores and selected paths.',
    icon: Users,
    highlight: '[data-testid="child-card"]',
    position: 'right'
  },
  {
    id: 'decisions',
    title: 'Family Decision Workflow',
    description: 'Track your children\'s path decisions here. When they confirm a path, you\'ll be able to Support, Consider, or provide feedback.',
    icon: Target,
    highlight: 'a[href="/app/family/decisions"]',
    position: 'right'
  },
  {
    id: 'research',
    title: 'Research Pathways',
    description: 'Explore all educational options together - colleges, skilled trades, military service, entrepreneurship, and future careers.',
    icon: GraduationCap,
    highlight: '[data-section="research"]',
    position: 'right'
  },
  {
    id: 'financial',
    title: 'Financial Intelligence',
    description: 'Plan for education costs, track savings, and explore funding scenarios. Get a clear picture of what each path will cost.',
    icon: DollarSign,
    highlight: 'a[href="/app/parent/intelligence"]',
    position: 'right'
  },
  {
    id: 'sara',
    title: 'Meet Sara - Your AI Advisor',
    description: 'Click "Ask Sara" anytime to get personalized guidance on supporting your children, financial planning, and more.',
    icon: MessageSquare,
    highlight: '[data-testid="patricia-chat-btn"]',
    position: 'left'
  },
  {
    id: 'complete',
    title: 'You\'re All Set! ✨',
    description: 'You now know the essentials. Start by exploring your children\'s profiles or checking out the pathway options.',
    icon: CheckCircle,
    position: 'center'
  }
];

const STUDENT_TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Agentic FuturePath AI! 🎉',
    description: 'Let\'s take a quick tour to help you navigate your educational journey. This will only take 2 minutes!',
    icon: Home,
    position: 'center'
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description: 'This is your home base. Track your progress, see your readiness score, and access all your tools from here.',
    icon: Home,
    position: 'right'
  },
  {
    id: 'paths',
    title: 'My Paths',
    description: 'Select and track your educational paths here. Whether it\'s college, trades, military, or entrepreneurship - this is where you commit to your future.',
    icon: Target,
    position: 'right'
  },
  {
    id: 'explore',
    title: 'Explore Options',
    description: 'Research colleges, careers, and programs. Find what fits your interests and goals.',
    icon: GraduationCap,
    position: 'right'
  },
  {
    id: 'patricia',
    title: 'Meet Patricia - Your AI Counselor',
    description: 'Click "Ask Patricia" anytime for personalized guidance on college planning, career exploration, and more.',
    icon: MessageSquare,
    highlight: '[data-testid="patricia-chat-btn"]',
    position: 'left'
  },
  {
    id: 'complete',
    title: 'You\'re Ready! ✨',
    description: 'Start exploring your options and building your future. Your journey begins now!',
    icon: CheckCircle,
    position: 'center'
  }
];

export default function GuidedTour({ isOpen, onClose, userRole }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenTour, setHasSeenTour] = useState(false);
  
  const steps = userRole === 'parent' ? PARENT_TOUR_STEPS : STUDENT_TOUR_STEPS;
  const step = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  
  useEffect(() => {
    // Check if user has seen the tour
    const seen = localStorage.getItem(`futurepath_tour_${userRole}`);
    setHasSeenTour(!!seen);
  }, [userRole]);
  
  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleComplete = () => {
    localStorage.setItem(`futurepath_tour_${userRole}`, 'true');
    setHasSeenTour(true);
    onClose();
  };
  
  const handleSkip = () => {
    localStorage.setItem(`futurepath_tour_${userRole}`, 'skipped');
    onClose();
  };
  
  if (!isOpen) return null;
  
  const Icon = step.icon;
  
  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100]">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-[#0a1729]/70"
          onClick={handleSkip}
        />
        
        {/* Tour Card - always centered */}
        <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="w-full max-w-md pointer-events-auto"
          >
          <div className="bg-slate-900 border border-blue-500/30 rounded-2xl shadow-2xl shadow-blue-500/20 overflow-hidden">
            {/* Progress Bar */}
            <div className="h-1 bg-slate-800">
              <motion.div 
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                initial={{ width: 0 }}
                animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            
            {/* Content */}
            <div className="p-6">
              {/* Icon & Step Counter */}
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-300 text-sm">
                    {currentStep + 1} of {steps.length}
                  </span>
                  <button 
                    onClick={handleSkip}
                    className="text-slate-300 hover:text-slate-300 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Title & Description */}
              <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
              <p className="text-slate-300 leading-relaxed mb-6">{step.description}</p>
              
              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  className="text-slate-300 hover:text-slate-200"
                >
                  Skip Tour
                </Button>
                
                <div className="flex items-center gap-2">
                  {!isFirstStep && (
                    <Button
                      variant="outline"
                      onClick={handlePrev}
                      className="border-slate-600"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                  )}
                  <Button
                    onClick={handleNext}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500"
                  >
                    {isLastStep ? (
                      <>Get Started <CheckCircle className="w-4 h-4 ml-1" /></>
                    ) : (
                      <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
                    )}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Step Dots */}
            <div className="flex justify-center gap-1.5 pb-4">
              {steps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep 
                      ? 'w-6 bg-blue-500' 
                      : idx < currentStep
                        ? 'bg-blue-500/50'
                        : 'bg-slate-700'
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>
        </div>
        {/* Pointer Arrow (for non-center positions) */}
        {step.position !== 'center' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`absolute ${
              step.position === 'right'
                ? 'left-[300px] top-1/2 -translate-y-1/2'
                : 'right-[360px] top-24'
            }`}
          >
            <div className={`w-4 h-4 bg-slate-900 border-l border-t border-blue-500/30 ${
              step.position === 'right' ? '-rotate-45' : 'rotate-[135deg]'
            }`} />
          </motion.div>
        )}
      </div>
    </AnimatePresence>
  );
}

// Hook to manage tour state
export function useTour(userRole) {
  const [showTour, setShowTour] = useState(false);
  
  // The Signature Welcome is the first-run experience now, so this tour never
  // auto-opens over the top of it. It stays available from the Tour button.
  
  const startTour = () => setShowTour(true);
  const closeTour = () => setShowTour(false);
  const resetTour = () => {
    localStorage.removeItem(`futurepath_tour_${userRole}`);
    setShowTour(true);
  };
  
  return { showTour, startTour, closeTour, resetTour };
}
